import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import {
  CalendarBlankIcon,
  DotsSixVerticalIcon,
  MapPinIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import type { Experience } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";

import { formatExperiencePeriod, stripHtml } from "./date-utils";

type Props = {
  item: Experience;
  onEdit: (item: Experience) => void;
  onDelete: (id: string) => void;
};

export const ExperienceCard = ({ item, onEdit, onDelete }: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const contractType = (item as Experience & { contractType?: string }).contractType;

  return (
    <article
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
      }}
      className="group grid grid-cols-[28px_minmax(0,1fr)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_7px_22px_rgba(15,23,42,0.04)]"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Réorganiser cette expérience"
        className="grid cursor-grab place-items-center border-r border-slate-100 text-slate-300 hover:bg-indigo-50 hover:text-indigo-500 active:cursor-grabbing"
      >
        <DotsSixVerticalIcon size={14} weight="bold" />
      </button>
      <div className="min-w-0 p-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-xs font-bold text-indigo-950 sm:text-sm">
                {item.position || "Poste non renseigné"}
              </h3>
              {item.isPresent && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[8px] font-bold text-emerald-700">
                  Poste actuel
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate text-[11px] font-semibold text-indigo-600 sm:text-xs">
              {item.company || "Entreprise non renseignée"}
            </p>
          </div>
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={() => onEdit(item)}
              aria-label="Modifier l’expérience"
              className="grid size-8 place-items-center rounded-lg text-indigo-600 hover:bg-indigo-50"
            >
              <PencilSimpleIcon size={15} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(item.id)}
              aria-label="Supprimer l’expérience"
              className="grid size-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            >
              <TrashIcon size={15} />
            </button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[9px] font-medium text-slate-500 sm:text-[10px]">
          <span className="inline-flex items-center gap-1">
            <CalendarBlankIcon size={12} />
            {formatExperiencePeriod(
              item.startDate ?? "",
              item.endDate ?? "",
              Boolean(item.isPresent),
            ) || "Période non renseignée"}
          </span>
          {item.location && (
            <span className="inline-flex items-center gap-1">
              <MapPinIcon size={12} />
              {item.location}
            </span>
          )}
          {contractType && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5">{contractType}</span>
          )}
        </div>
        {stripHtml(item.summary) && (
          <p className="mt-2 line-clamp-2 text-[10px] leading-4 text-slate-600 sm:text-[11px]">
            {stripHtml(item.summary)}
          </p>
        )}
      </div>
    </article>
  );
};
